import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import usePermission from '../../../hooks/usePermission';

const EmployeeListing = () => {
  const { hasPermission } = usePermission();

  const [employees, setEmployees] = useState([]);
  const [masterRoles, setMasterRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters & Pagination
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Add/Edit Modal
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentEmpId, setCurrentEmpId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    password: '',
    master_role_id: '',
    status: 'active'
  });
  const [submitting, setSubmitting] = useState(false);

  // Reset Password Modal
  const [resetModal, setResetModal] = useState({ show: false, employee: null });
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  // Delete Modal
  const [deleteModal, setDeleteModal] = useState({ show: false, employee: null });

  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('superAdminToken')}` }
  });

  const fetchMasterRoles = async () => {
    try {
      const res = await axios.get(`${API_BASE}/superadmin/roles`, getAuthHeader());
      setMasterRoles(res.data.roles || []);
    } catch (err) {
      console.error('Failed to load master roles', err);
    }
  };

  const fetchEmployees = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 10,
        search,
        role_id: roleFilter,
        status: statusFilter
      };

      const res = await axios.get(`${API_BASE}/superadmin/employees`, {
        ...getAuthHeader(),
        params
      });

      setEmployees(res.data.employees || []);
      setTotalPages(res.data.totalPages || 1);
      setTotalCount(res.data.totalCount || 0);
      setCurrentPage(res.data.currentPage || 1);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch employees');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMasterRoles();
  }, []);

  useEffect(() => {
    fetchEmployees(1);
  }, [search, roleFilter, statusFilter]);

  const handleOpenAddModal = () => {
    setIsEditing(false);
    setCurrentEmpId(null);
    setFormData({
      name: '',
      email: '',
      mobile: '',
      password: '',
      master_role_id: masterRoles.length > 0 ? masterRoles[0].id : '',
      status: 'active'
    });
    setShowModal(true);
  };

  const handleOpenEditModal = (emp) => {
    setIsEditing(true);
    setCurrentEmpId(emp.id);
    setFormData({
      name: emp.name,
      email: emp.email,
      mobile: emp.mobile || '',
      password: '', // Blank unless updating
      master_role_id: emp.master_role_id || '',
      status: emp.status
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isEditing) {
        await axios.put(`${API_BASE}/superadmin/employees/${currentEmpId}`, formData, getAuthHeader());
        toast.success('Employee updated successfully');
      } else {
        await axios.post(`${API_BASE}/superadmin/employees`, formData, getAuthHeader());
        toast.success('Employee created successfully! First-login reset required.');
      }
      setShowModal(false);
      fetchEmployees(currentPage);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (emp) => {
    const newStatus = emp.status === 'active' ? 'inactive' : 'active';
    try {
      await axios.put(
        `${API_BASE}/superadmin/employees/${emp.id}/status`,
        { status: newStatus },
        getAuthHeader()
      );
      toast.success(`Employee ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchEmployees(currentPage);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change status');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setResetting(true);
    try {
      await axios.post(
        `${API_BASE}/superadmin/employees/${resetModal.employee.id}/reset-password`,
        { new_password: newPassword },
        getAuthHeader()
      );
      toast.success('Password reset successfully! Employee will be prompted to reset on next login.');
      setResetModal({ show: false, employee: null });
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const handleDeleteEmployee = async () => {
    if (!deleteModal.employee) return;
    try {
      await axios.delete(`${API_BASE}/superadmin/employees/${deleteModal.employee.id}`, getAuthHeader());
      toast.success('Employee deleted successfully');
      setDeleteModal({ show: false, employee: null });
      fetchEmployees(currentPage);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete employee');
    }
  };

  return (
    <div className="page-content">
      <div className="container-fluid">

        {/* Header Title */}
        <div className="row">
          <div className="col-12">
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <h4 className="mb-sm-0">Employee Management</h4>
              <div className="page-title-right">
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item"><Link to="/superadmin/dashboard">Dashboard</Link></li>
                  <li className="breadcrumb-item active">Employees</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="row mb-3 g-3">
          <div className="col-lg-4">
            <div className="position-relative">
              <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-16" style={{ pointerEvents: 'none', zIndex: 5 }}></i>
              <input
                type="text"
                className="form-control ps-5"
                style={{ paddingLeft: '45px' }}
                placeholder="Search by name, email, or mobile..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="col-lg-3 col-md-4">
            <select
              className="form-select"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="">All Master Roles</option>
              {masterRoles.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>

          <div className="col-lg-2 col-md-4">
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="col-lg-3 col-md-4 text-md-end">
            {hasPermission('employees.add') && (
              <button className="btn btn-primary w-100 w-md-auto" onClick={handleOpenAddModal}>
                <i className="ri-user-add-line align-bottom me-1"></i> Add Employee
              </button>
            )}
          </div>
        </div>

        {/* Employees Table */}
        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-body">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status"></div>
                    <p className="mt-2 text-muted">Loading employees...</p>
                  </div>
                ) : employees.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <i className="ri-account-circle-line display-4 text-secondary"></i>
                    <p className="mt-2 fs-15">No employees found matching criteria.</p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>Employee Name</th>
                            <th>Email</th>
                            <th>Mobile</th>
                            <th>Master Role</th>
                            <th>Status</th>
                            <th>Password Flag</th>
                            <th className="text-end">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {employees.map((emp, idx) => {
                            const isSuperAdminRow = emp.role_type === 'superadmin' || !emp.master_role_id || (emp.MasterRole?.name && emp.MasterRole.name.toLowerCase().includes('super'));

                            return (
                              <tr key={emp.id}>
                                <td>{(currentPage - 1) * 10 + idx + 1}</td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <div className="avatar-xs me-2">
                                      <span className="avatar-title rounded-circle bg-primary-subtle text-primary fw-bold">
                                        {emp.name ? emp.name.charAt(0).toUpperCase() : 'E'}
                                      </span>
                                    </div>
                                    <span className="fw-bold text-dark">{emp.name}</span>
                                  </div>
                                </td>
                                <td>{emp.email}</td>
                                <td>{emp.mobile || <span className="text-muted fs-12">N/A</span>}</td>
                                <td>
                                  {emp.MasterRole ? (
                                    <span className={`badge ${emp.MasterRole.status === 'active' ? 'bg-primary-subtle text-primary' : 'bg-warning-subtle text-warning'} px-2 py-1 fs-12`}>
                                      <i className="ri-shield-user-line me-1"></i>
                                      {emp.MasterRole.name}
                                    </span>
                                  ) : isSuperAdminRow ? (
                                    <span className="badge bg-danger-subtle text-danger px-2 py-1 fs-12">
                                      <i className="ri-shield-user-line me-1"></i>
                                      Super Admin
                                    </span>
                                  ) : (
                                    <span className="badge bg-secondary-subtle text-secondary">Unassigned</span>
                                  )}
                                </td>
                                <td>
                                  {emp.status === 'active' ? (
                                    <span className="badge bg-success-subtle text-success">Active</span>
                                  ) : (
                                    <span className="badge bg-danger-subtle text-danger">Inactive</span>
                                  )}
                                </td>
                                <td>
                                  {emp.must_change_password ? (
                                    <span className="badge bg-warning-subtle text-warning" title="Password change required on next login">
                                      <i className="ri-key-line me-1"></i> Reset Required
                                    </span>
                                  ) : (
                                    <span className="badge bg-success-subtle text-success">Normal</span>
                                  )}
                                </td>
                                <td className="text-end">
                                  <div className="d-flex gap-2 justify-content-end">
                                    {isSuperAdminRow ? (
                                      hasPermission('employees.reset_password') && (
                                        <button
                                          className="btn btn-sm btn-soft-info"
                                          onClick={() => setResetModal({ show: true, employee: emp })}
                                          title="Reset Password"
                                        >
                                          <i className="ri-key-2-line"></i>
                                        </button>
                                      )
                                    ) : (
                                      <>
                                        {hasPermission('employees.change_status') && (
                                          <button
                                            className={`btn btn-sm ${emp.status === 'active' ? 'btn-soft-warning' : 'btn-soft-success'}`}
                                            onClick={() => handleToggleStatus(emp)}
                                            title={emp.status === 'active' ? 'Deactivate Employee' : 'Activate Employee'}
                                          >
                                            <i className={emp.status === 'active' ? 'ri-user-unfollow-line' : 'ri-user-follow-line'}></i>
                                          </button>
                                        )}
                                        {hasPermission('employees.reset_password') && (
                                          <button
                                            className="btn btn-sm btn-soft-info"
                                            onClick={() => setResetModal({ show: true, employee: emp })}
                                            title="Reset Password"
                                          >
                                            <i className="ri-key-2-line"></i>
                                          </button>
                                        )}
                                        {hasPermission('employees.edit') && (
                                          <button
                                            className="btn btn-sm btn-soft-primary"
                                            onClick={() => handleOpenEditModal(emp)}
                                            title="Edit Employee"
                                          >
                                            <i className="ri-pencil-line"></i>
                                          </button>
                                        )}
                                        {hasPermission('employees.delete') && (
                                          <button
                                            className="btn btn-sm btn-soft-danger"
                                            onClick={() => setDeleteModal({ show: true, employee: emp })}
                                            title="Delete Employee"
                                          >
                                            <i className="ri-delete-bin-line"></i>
                                          </button>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <span className="text-muted fs-13">Showing {employees.length} of {totalCount} employees</span>
                        <ul className="pagination pagination-sm mb-0">
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => fetchEmployees(currentPage - 1)}>Previous</button>
                          </li>
                          {[...Array(totalPages)].map((_, i) => (
                            <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                              <button className="page-link" onClick={() => fetchEmployees(i + 1)}>{i + 1}</button>
                            </li>
                          ))}
                          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => fetchEmployees(currentPage + 1)}>Next</button>
                          </li>
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Add / Edit Employee Modal */}
        {showModal && (
          <div className="modal fade show d-block tab-modal-backdrop" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{isEditing ? 'Edit Employee Details' : 'Create New Employee Account'}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Full Name <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">Email Address <span className="text-danger">*</span></label>
                      <input
                        type="email"
                        className="form-control"
                        placeholder="employee@mangoloops.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">Mobile Number</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="+91 9876543210"
                        value={formData.mobile}
                        onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                      />
                    </div>

                    {!isEditing && (
                      <div className="mb-3">
                        <label className="form-label fw-semibold">Initial Temporary Password <span className="text-danger">*</span></label>
                        <input
                          type="password"
                          className="form-control"
                          placeholder="Minimum 6 characters"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          required={!isEditing}
                        />
                        <small className="text-muted">Employee will be forced to change this password on first login.</small>
                      </div>
                    )}

                    <div className="mb-3">
                      <label className="form-label fw-semibold">Master Role <span className="text-danger">*</span></label>
                      <select
                        className="form-select"
                        value={formData.master_role_id}
                        onChange={(e) => setFormData({ ...formData, master_role_id: e.target.value })}
                        required
                      >
                        <option value="">Select Master Role</option>
                        {masterRoles.map(r => (
                          <option key={r.id} value={r.id}>{r.name} ({r.status})</option>
                        ))}
                      </select>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">Status</label>
                      <select
                        className="form-select"
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button type="button" className="btn btn-light" onClick={() => setShowModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={submitting}>
                      {submitting ? 'Saving...' : (isEditing ? 'Update Employee' : 'Create Employee')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Reset Password Modal */}
        {resetModal.show && (
          <div className="modal fade show d-block tab-modal-backdrop" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header bg-info text-white">
                  <h5 className="modal-title text-white">Reset Employee Password</h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setResetModal({ show: false, employee: null })}></button>
                </div>
                <form onSubmit={handleResetPassword}>
                  <div className="modal-body py-4">
                    <p className="mb-3">Resetting password for <strong>{resetModal.employee?.name}</strong> ({resetModal.employee?.email}).</p>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">New Temporary Password</label>
                      <input
                        type="password"
                        className="form-control"
                        placeholder="Enter new password (min 6 characters)"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                      />
                    </div>
                    <p className="text-muted fs-13 mb-0">Note: This will flag the employee account to reset their password upon next login.</p>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-light" onClick={() => setResetModal({ show: false, employee: null })}>Cancel</button>
                    <button type="submit" className="btn btn-info text-white" disabled={resetting}>
                      {resetting ? 'Resetting...' : 'Set New Password'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteModal.show && (
          <div className="modal fade show d-block tab-modal-backdrop" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header bg-danger text-white">
                  <h5 className="modal-title text-white">Confirm Delete Employee</h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setDeleteModal({ show: false, employee: null })}></button>
                </div>
                <div className="modal-body text-center py-4">
                  <i className="ri-user-unfollow-line text-danger display-4 mb-2"></i>
                  <h5>Are you sure you want to delete employee '{deleteModal.employee?.name}'?</h5>
                  <p className="text-muted fs-14">This will permanently remove access for {deleteModal.employee?.email}. This action cannot be undone.</p>
                </div>
                <div className="modal-footer justify-content-center">
                  <button type="button" className="btn btn-light" onClick={() => setDeleteModal({ show: false, employee: null })}>Cancel</button>
                  <button type="button" className="btn btn-danger" onClick={handleDeleteEmployee}>Yes, Delete Employee</button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default EmployeeListing;
